'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  EyeIcon,
  DownloadIcon,
  MoreHorizontalIcon,
  ClipboardListIcon,
  Trash2Icon,
  SquareMenuIcon,
  MonitorIcon,
  UserIcon,
  ActivityIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog, LoadingState, SortableHeader } from '@/components/partials'
import { AuditLogsFilters } from '@/components/partials/audit-logs'
import { useAuditLogs, useDeleteAuditLog, useBulkDeleteAuditLogs, useExportAuditLogs } from '@/hooks'
import { useAuditLogsStore, useMeStore } from '@/store'
import { AuditLogsFilterParams } from '@/types/audit-log'
import { AuditResultEnum, SortEnum } from '@/enums'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canListUsers = hasPermission(currentUser, 'USERS', 'LIST')
  const canExport = hasPermission(currentUser, 'AUDIT_LOGS', 'LIST') && hasPermission(currentUser, 'AUDIT_LOGS', 'SHOW')

  const [deleteAuditLogId, setDeleteAuditLogId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const selectedAuditLogIds = useAuditLogsStore(state => state.selectedAuditLogIds)
  const toggleAuditLogSelection = useAuditLogsStore(state => state.toggleAuditLogSelection)
  const selectAllAuditLogs = useAuditLogsStore(state => state.selectAllAuditLogs)
  const deselectAllAuditLogs = useAuditLogsStore(state => state.deselectAllAuditLogs)
  const isAuditLogSelected = useAuditLogsStore(state => state.isAuditLogSelected)

  const filters = useMemo<AuditLogsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    const modulesParam = params.get('modules')
    const actionsParam = params.get('actions')
    const resultsParam = params.get('results')
    const userIdsParam = params.get('userIds')

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: (params.get('sortBy') as AuditLogsFilterParams['sortBy']) || 'createdAt',
      q: params.get('q') || undefined,
      modules: modulesParam ? (modulesParam.split(',').filter(Boolean) as any) : undefined,
      actions: actionsParam ? (actionsParam.split(',').filter(Boolean) as any) : undefined,
      results: resultsParam ? (resultsParam.split(',').filter(Boolean) as any) : undefined,
      userIds: userIdsParam ? userIdsParam.split(',').filter(Boolean) : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useAuditLogs(filters)
  const { mutate: deleteAuditLogMutate, isPending: isDeleting } = useDeleteAuditLog()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteAuditLogs()
  const { mutate: exportMutate, isPending: isExporting } = useExportAuditLogs()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<AuditLogsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<AuditLogsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/logs/audit')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy: sortBy as AuditLogsFilterParams['sortBy'], sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/logs/audit/${id}`)
  }

  const handleDelete = (id: string) => {
    setDeleteAuditLogId(id)
  }

  const handleBulkDelete = () => {
    if (selectedAuditLogIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const handleSelectAll = () => {
    if (data && data.items.length > 0) {
      if (selectedAuditLogIds.length === data.items.length) {
        deselectAllAuditLogs()
      } else {
        selectAllAuditLogs(data.items)
      }
    }
  }

  const confirmDeleteAuditLog = () => {
    if (deleteAuditLogId) {
      deleteAuditLogMutate(deleteAuditLogId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
          toast.success(t('deleted_successfully'))
          setDeleteAuditLogId(null)
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.message ?? t('error_deleting'))
          setDeleteAuditLogId(null)
        }
      })
    }
  }

  const confirmBulkDeleteAuditLogs = () => {
    if (selectedAuditLogIds.length > 0) {
      bulkDeleteMutate(selectedAuditLogIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            toast.error(error.response.data?.items?.ids || t('error_deleting'))
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('audit_log_details')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardListIcon className="h-5 w-5" />
                {t('audit_logs')}
              </CardTitle>
              <CardDescription>{t('audit_logs_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {selectedAuditLogIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedAuditLogIds.length})</span>
                  </div>
                </Button>
              )}
              {canExport && (
                <Button variant="outline" onClick={() => exportMutate(filters)} disabled={isExporting}>
                  <DownloadIcon className="h-4 w-4" />
                  <span className="hidden lg:inline">{t('export_as_excel')}</span>
                </Button>
              )}
              <AuditLogsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={data.items.length > 0 && selectedAuditLogIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        {canListUsers && (
                          <TableHead className="min-w-40">
                            <SortableHeader
                              sortBy="userId"
                              currentSortBy={filters.sortBy}
                              currentSort={filters.sort}
                              onSort={handleSort}>
                              <UserIcon className="h-4 w-4" />
                              {t('user')}
                            </SortableHeader>
                          </TableHead>
                        )}
                        <TableHead className="min-w-36 text-center">
                          <SortableHeader
                            sortBy="module"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ActivityIcon className="h-4 w-4" />
                            {t('module')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-28 text-center">
                          <SortableHeader
                            sortBy="action"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('action')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-24 text-center">
                          <SortableHeader
                            sortBy="result"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('result')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="ip"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <MonitorIcon className="h-4 w-4" />
                            {t('ip')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('action_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canListUsers ? 8 : 7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((auditLog, index) => (
                          <ContextMenu key={auditLog.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(auditLog.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={isAuditLogSelected(auditLog.id)}
                                      onCheckedChange={() => toggleAuditLogSelection(auditLog.id)}
                                      onClick={e => e.stopPropagation()}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                {canListUsers && (
                                  <TableCell>
                                    {auditLog.user ? (
                                      <div>
                                        <p className="font-medium">{auditLog.user.fullName}</p>
                                        <p className="text-muted-foreground text-xs">{auditLog.user.email}</p>
                                      </div>
                                    ) : (
                                      <span className="font-medium">{auditLog.email}</span>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell className="text-center">
                                  <Badge variant="outline">{t(`module_${auditLog.module}` as any)}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="secondary">{t(`audit_action_${auditLog.action}` as any)}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge
                                    variant={auditLog.result === AuditResultEnum.SUCCESS ? 'success' : 'destructive'}>
                                    {t(`audit_result_${auditLog.result}` as any)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center font-mono text-sm">{auditLog.ip}</TableCell>
                                <TableCell className="text-center">
                                  {formatDateTime(auditLog.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={e => e.stopPropagation()}
                                        className="h-8 w-8 p-0">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(auditLog.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(auditLog.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteAuditLogId}
        onCancel={() => setDeleteAuditLogId(null)}
        onConfirm={confirmDeleteAuditLog}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteAuditLogs}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
